import {useEffect, useRef} from 'react'
import {X} from 'lucide-react'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  children: React.ReactNode
  size?: 'md' | 'lg' | 'xl' | 'full'
}

const sizeClasses = {
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  full: 'max-w-[90vw]',
}

export default function Modal({isOpen, onClose, title, children, size = 'lg'}: ModalProps) {
  const overlayRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    if (isOpen) window.addEventListener('keydown', handleEsc)
    return () => window.removeEventListener('keydown', handleEsc)
  }, [isOpen, onClose])

  if (!isOpen) return null

  return (
    <div
      ref={overlayRef}
      className='fixed inset-0 z-50 flex items-center justify-center px-4 animate-fade-in'
      onClick={(e) => {
        if (e.target === overlayRef.current) onClose()
      }}
      role='dialog'
      aria-modal='true'
      aria-label={title}
    >
      <div className='fixed inset-0 bg-slate-900/50 backdrop-blur-sm' />
      <div
        className={`devops-surface relative w-full ${sizeClasses[size]} max-h-[88vh] flex flex-col animate-scale-in`}
      >
        {title && (
          <div className='flex items-center justify-between px-6 py-4 border-b border-slate-100'>
            <h2 className='text-lg font-semibold text-slate-800'>{title}</h2>
            <button
              onClick={onClose}
              className='p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors'
              aria-label='Fechar modal'
            >
              <X size={20} />
            </button>
          </div>
        )}
        <div className='overflow-y-auto flex-1'>{children}</div>
      </div>
    </div>
  )
}
